import { domainError, SHARED_ERROR, type DomainError } from './domain-error';
import { err, ok, type Result } from './result';

/** Quartos de hora em uma hora. Ver ADR-106. */
export const QUARTERS_PER_HOUR = 4;

/**
 * Quantidade de horas de trabalho.
 *
 * Armazenada e calculada em **quartos de hora inteiros** — 9h = 36 quartos.
 * JavaScript não tem decimal: somar 0.25 em ponto flutuante ao longo de uma semana
 * produz `44.99999999999999h` e destrói a confiança no sistema inteiro. Com inteiros,
 * a exatidão é propriedade do tipo, não resultado de cuidado.
 *
 * A granularidade de 15 minutos da RN-048 deixa de ser validação e passa a ser
 * impossibilidade: não há como representar 7 minutos.
 *
 * Valores negativos são permitidos porque saldo negativo é informação legítima
 * (sobrecarga). Quem exige positivo valida explicitamente com `isPositive()`.
 */
export class Hours {
  static readonly ZERO = new Hours(0);

  private constructor(readonly quarters: number) {}

  static fromQuarters(quarters: number): Result<Hours, DomainError> {
    if (!Number.isInteger(quarters)) {
      return err(
        domainError(SHARED_ERROR.INVALID_HOURS, 'Horas devem ser um número inteiro de quartos.', {
          quarters,
        }),
      );
    }
    return ok(new Hours(quarters));
  }

  /** Aceita 9, 4.5, 0.25. Recusa 0.1 e qualquer valor fora da grade de 15 minutos. */
  static fromHours(hours: number): Result<Hours, DomainError> {
    if (!Number.isFinite(hours)) {
      return err(domainError(SHARED_ERROR.INVALID_HOURS, 'Valor de horas inválido.', { hours }));
    }
    const quarters = hours * QUARTERS_PER_HOUR;
    // Tolerância mínima contra representação binária de 0.25, 0.5 e 0.75.
    const rounded = Math.round(quarters);
    if (Math.abs(quarters - rounded) > 1e-9) {
      return err(
        domainError(
          SHARED_ERROR.INVALID_HOURS,
          'Horas devem ser múltiplas de 15 minutos (0,25h).',
          { hours },
        ),
      );
    }
    return ok(new Hours(rounded));
  }

  /** Atalho para literais confiáveis (constantes, seed, teste). Lança se inválido. */
  static ofQuarters(quarters: number): Hours {
    const result = Hours.fromQuarters(quarters);
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  }

  static sum(values: readonly Hours[]): Hours {
    let total = 0;
    for (const value of values) total += value.quarters;
    return new Hours(total);
  }

  plus(other: Hours): Hours {
    return new Hours(this.quarters + other.quarters);
  }

  minus(other: Hours): Hours {
    return new Hours(this.quarters - other.quarters);
  }

  times(factor: number): Result<Hours, DomainError> {
    return Hours.fromQuarters(this.quarters * factor);
  }

  negated(): Hours {
    return new Hours(-this.quarters);
  }

  compare(other: Hours): number {
    return this.quarters - other.quarters;
  }

  equals(other: Hours): boolean {
    return this.quarters === other.quarters;
  }

  isZero(): boolean {
    return this.quarters === 0;
  }

  isPositive(): boolean {
    return this.quarters > 0;
  }

  isNegative(): boolean {
    return this.quarters < 0;
  }

  greaterThan(other: Hours): boolean {
    return this.quarters > other.quarters;
  }

  lessThan(other: Hours): boolean {
    return this.quarters < other.quarters;
  }

  /** Valor decimal para exibição e exportação. Nunca use para acumular. */
  toDecimal(): number {
    return this.quarters / QUARTERS_PER_HOUR;
  }

  /** `36 → "9h"` · `18 → "4h30"` · `1 → "0h15"` · `-9 → "-2h15"` */
  format(): string {
    const negative = this.quarters < 0;
    const abs = Math.abs(this.quarters);
    const wholeHours = Math.floor(abs / QUARTERS_PER_HOUR);
    const minutes = (abs % QUARTERS_PER_HOUR) * 15;
    const body =
      minutes === 0 ? `${wholeHours}h` : `${wholeHours}h${String(minutes).padStart(2, '0')}`;
    return negative ? `-${body}` : body;
  }

  toString(): string {
    return this.format();
  }
}
